import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { McpServerInitializeResultSchema } from "../../../../schemas/McpServer/initializeResult/__json.schema";
import { McpToolSchemaHashSchema } from "../../../../schemas/McpServerTool/schemaHash.schema";
import { McpInstructionsApprovalStateSchema } from "../../../../shared/domain/mcp/mcp-instructions-approval-state";
import { McpSyncErrorCodeSchema } from "../../../../shared/domain/mcp/mcp-sync-error-code";
import { McpToolDriftReportSchema } from "../../../../shared/domain/mcp/mcp-tool-drift";
import { McpServerToolViewSchema } from "./mcp-server-tool.schemas";

/** The sync state every server view carries, in one place so the two agree. */
const McpSyncStateShape = {
  lastSyncAttemptedAt: z.coerce.date().nullable(),
  lastSyncSucceededAt: z.coerce.date().nullable(),
  lastSyncOutcome: z.enum(DbEnum.MCP_SYNC_OUTCOME_VALUES).nullable(),
  /**
   * Parsed with the CURRENT vocabulary, and tolerant of one it does not know:
   * the column is a string precisely so a new failure class needs no migration,
   * and a pod on an older release reads codes a newer one wrote. A strict enum
   * here would turn that into a parse failure on a list endpoint.
   */
  lastSyncErrorCode: z.union([McpSyncErrorCodeSchema, z.string()]).nullable()
} as const;

/**
 * The state of the OAuth credential this server presents, in one place so the two
 * views agree.
 *
 * ## Why the sync columns above are not enough
 *
 * They are a verdict about the credential that existed AT `lastSyncAttemptedAt`.
 * A sync that found no usable grant records `AUTHORIZATION_REQUIRED` and never
 * revisits that column, so finishing the authorization afterwards leaves the row
 * saying the connection failed over a credential that is now ACTIVE. A surface
 * reading only those columns therefore tells an administrator their connection
 * did not work at the exact moment it did. Carrying the credential's own state —
 * and the instant it was last written — is what lets a client date the sync's
 * verdict and ignore an authorization complaint that predates the grant.
 *
 * ## 🔴 Neither field is a secret, and the read that fills them takes no column
 * that could be
 *
 * `ToolCredentials` holds `accessToken`, `refreshToken` and `apikey`. The port
 * behind these two selects `externalToolId`, `status`, `updatedAt` and
 * `createdAt` and nothing else, for the same reason
 * `FindMcpServerOAuthCredentialIdPort` selects only an id: a credential whose
 * envelope will not open must still be describable. `status` is an enum of four
 * fixed tokens and an instant is an instant, so neither can carry a grant.
 */
const McpOAuthCredentialShape = {
  /**
   * `ToolCredentials.status` for this server's `oauth` credential, or `null`.
   *
   * `null` covers three states a screen cannot act on differently: the server is
   * not `OAUTH` at all, no credential row exists yet, and a row exists whose
   * nullable `status` column was never written. All three mean "we hold no
   * authorization for this server", which is the only distinction a client makes.
   *
   * `ACTIVE` is the one value that means authorized. `PENDING` is a flow that was
   * started and never came back; `NEEDS_REAUTH` is a grant the authorization
   * server has refused and only a new authorization repairs.
   */
  oauthCredentialStatus: z.enum(DbEnum.OAUTH_CREDENTIALS_STATUS_VALUES).nullable(),
  /**
   * When that credential row was last written — `updatedAt`, falling back to
   * `createdAt` because `updatedAt` is nullable on this table.
   *
   * Deliberately NOT named for authorization: any write to the row moves it, so
   * it is the instant the row last CHANGED and not the instant a grant was
   * issued. That is precisely what it is needed for — comparing it against
   * `lastSyncAttemptedAt` answers "did the sync see the credential we hold now",
   * which is the only question that separates a stale authorization complaint
   * from a live one.
   */
  oauthCredentialUpdatedAt: z.coerce.date().nullable()
} as const;

/** One registered server in the list — no tools, no initialize result. */
export const McpServerSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  url: z.string(),
  transport: z.enum(DbEnum.MCP_TRANSPORT_VALUES),
  authMode: z.enum(DbEnum.MCP_AUTH_MODE_VALUES),
  authHeaderName: z.string().nullable(),
  ...McpSyncStateShape,
  ...McpOAuthCredentialShape,
  instructionsState: McpInstructionsApprovalStateSchema,
  /**
   * Per-status tool counts, keyed by the enum rather than a handful of named
   * numbers — so adding a status to `McpToolStatus` cannot leave a count silently
   * uncollected. The producer types it as `Record<$Enums.McpToolStatus, number>`,
   * which fails to typecheck until every member has an entry.
   */
  toolCounts: z.record(z.enum(DbEnum.MCP_TOOL_STATUS_VALUES), z.number().int().nonnegative()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type McpServerSummary = z.infer<typeof McpServerSummarySchema>;

/** One server with everything an approval screen needs. */
export const McpServerDetailSchema = McpServerSummarySchema.extend({
  /**
   * The server's own `initialize` result, verbatim minus `_meta` and
   * `instructions`. UNTRUSTED: every string in it is server-authored, so a client
   * renders it as text and never as markup.
   */
  initializeResult: McpServerInitializeResultSchema.nullable(),
  /** UNTRUSTED, and NOT what a model is given — see `instructionsState`. */
  discoveredInstructions: z.string().nullable(),
  discoveredInstructionsHash: McpToolSchemaHashSchema.nullable(),
  /** The only text ever given to a model, and only while `instructionsState` is APPROVED. */
  approvedInstructions: z.string().nullable(),
  approvedInstructionsHash: McpToolSchemaHashSchema.nullable(),
  instructionsApprovedAt: z.coerce.date().nullable(),
  instructionsApprovedByUserId: z.string().nullable(),
  tools: z.array(McpServerToolViewSchema),
  /**
   * What this server's advertised tools have done to the contract the
   * organization APPROVED: three lists of names, by `summariseMcpToolDrift`.
   *
   * It is the RE-SYNC report, and it is on the detail rather than on the sync
   * response alone because it is a STANDING statement about the current state and
   * not a delta of one discovery — see that function's header for why a delta is
   * correct exactly once. Carried on the detail, an administrator who opens the
   * server a week after the sync that found the drift sees the same three lists.
   *
   * It costs no query: it is derived from the `tools` array directly above it, in
   * `toMcpServerDetail`. That is also why it is NOT on `McpServerSummary` — the
   * list endpoint gets its census from one `groupBy` and never loads a tool name,
   * so putting names there would turn a list into a join over every tool of every
   * server. `toolCounts` already tells that screen WHICH servers have drift.
   */
  drift: McpToolDriftReportSchema
});

export type McpServerDetail = z.infer<typeof McpServerDetailSchema>;
